import { useState, useEffect } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import { apiClient } from '../../api/client';

interface ClusterClaim {
  id: string;
  content: string;
  claimType: string;
  isNovel: boolean;
  sourceCount?: number;
  createdAt: string;
  x: number;
  y: number;
}

interface ClusterEdge {
  source: number;
  target: number;
  similarity: number;
}

interface ClusterData {
  claims: ClusterClaim[];
  edges: ClusterEdge[];
  stats: {
    totalClaims: number;
    highSimilarityPairs: number;
  };
}

interface ClusterViewProps {
  storyId: string;
}

// Color scale based on claim type (uppercase to match database)
const typeColors: Record<string, string> = {
  NEWS: '#3b82f6',       // blue
  QUOTE: '#8b5cf6',      // purple
  OFFICIAL: '#10b981',   // green
  STATISTIC: '#06b6d4',  // cyan
  ALLEGATION: '#f59e0b', // amber
  FINDING: '#f97316',    // orange
  PREDICTION: '#ec4899', // pink
  ANALYSIS: '#6366f1',   // indigo
  default: '#6b7280',    // gray
};

// Custom tooltip component
function CustomTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: ClusterClaim }> }) {
  if (!active || !payload || !payload.length || !payload[0]) return null;

  const claim = payload[0].payload;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-w-sm">
      <p className="text-sm text-gray-900 font-medium line-clamp-3">{claim.content}</p>
      <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
        <span className={`px-1.5 py-0.5 rounded ${claim.isNovel ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
          {claim.isNovel ? 'NEW' : 'OLD'}
        </span>
        <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
          {claim.claimType || 'fact'}
        </span>
        {claim.sourceCount && claim.sourceCount > 1 && (
          <span>{claim.sourceCount} sources</span>
        )}
      </div>
    </div>
  );
}

export function ClusterView({ storyId }: ClusterViewProps) {
  const [data, setData] = useState<ClusterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedClaim, setSelectedClaim] = useState<ClusterClaim | null>(null);
  const [highlightSimilar, setHighlightSimilar] = useState(true);

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiClient.get<ClusterData>(`/stories/${storyId}/cluster`)
      .then(setData)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [storyId]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center h-64 text-gray-500">
          Loading cluster visualization...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Error loading cluster data: {error}
      </div>
    );
  }

  if (!data || data.claims.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center text-gray-500">
        No claims with embeddings available for visualization.
      </div>
    );
  }

  // Find claims similar to the selected one
  const getSimilarClaimIndices = (claimIndex: number): Set<number> => {
    if (!highlightSimilar) return new Set();
    const similar = new Set<number>();
    for (const edge of data.edges) {
      if (edge.source === claimIndex) similar.add(edge.target);
      if (edge.target === claimIndex) similar.add(edge.source);
    }
    return similar;
  };

  const selectedIndex = selectedClaim ? data.claims.findIndex(c => c.id === selectedClaim.id) : -1;
  const similarIndices = selectedIndex >= 0 ? getSimilarClaimIndices(selectedIndex) : new Set<number>();

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Claim Cluster Map</h2>
          <p className="text-sm text-gray-500 mt-1">
            {data.stats.totalClaims} claims · {data.stats.highSimilarityPairs} similar pairs ({">"}.75)
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={highlightSimilar}
            onChange={(e) => setHighlightSimilar(e.target.checked)}
            className="rounded border-gray-300"
          />
          Highlight similar on select
        </label>
      </div>

      <div className="p-4">
        {/* Legend */}
        <div className="flex flex-wrap gap-3 mb-4 text-xs">
          {Object.entries(typeColors).filter(([k]) => k !== 'default').map(([type, color]) => (
            <div key={type} className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
              <span className="text-gray-600">{type.charAt(0) + type.slice(1).toLowerCase()}</span>
            </div>
          ))}
        </div>

        {/* Chart */}
        <div className="h-[500px]">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
              <XAxis
                type="number"
                dataKey="x"
                domain={[-1.05, 1.05]}
                tick={false}
                axisLine={false}
                hide
              />
              <YAxis
                type="number"
                dataKey="y"
                domain={[-1.05, 1.05]}
                tick={false}
                axisLine={false}
                hide
              />
              <Tooltip content={<CustomTooltip />} />
              <Scatter
                data={data.claims}
                onClick={(point) => setSelectedClaim(point as ClusterClaim)}
              >
                {data.claims.map((claim, index) => {
                  const isSelected = selectedClaim?.id === claim.id;
                  const isSimilar = similarIndices.has(index);
                  const baseColor = typeColors[claim.claimType] || typeColors.default;

                  return (
                    <Cell
                      key={claim.id}
                      fill={isSelected ? '#ef4444' : isSimilar ? '#fbbf24' : baseColor}
                      stroke={isSelected ? '#b91c1c' : isSimilar ? '#d97706' : 'transparent'}
                      strokeWidth={isSelected || isSimilar ? 2 : 0}
                      r={isSelected ? 10 : isSimilar ? 8 : 6}
                      style={{ cursor: 'pointer' }}
                    />
                  );
                })}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        {/* Selected claim details */}
        {selectedClaim && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-900">{selectedClaim.content}</p>
                <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                  <span className={`px-1.5 py-0.5 rounded ${selectedClaim.isNovel ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {selectedClaim.isNovel ? 'NEW' : 'OLD'}
                  </span>
                  <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                    {selectedClaim.claimType || 'fact'}
                  </span>
                  {selectedClaim.sourceCount && selectedClaim.sourceCount > 1 && (
                    <span>{selectedClaim.sourceCount} sources</span>
                  )}
                </div>
                {similarIndices.size > 0 && (
                  <p className="mt-2 text-xs text-amber-600">
                    {similarIndices.size} similar claim{similarIndices.size !== 1 ? 's' : ''} highlighted
                  </p>
                )}
              </div>
              <button
                onClick={() => setSelectedClaim(null)}
                className="ml-4 p-1 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Similar pairs list */}
        {data.edges.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              High Similarity Pairs ({">"}0.75)
            </h3>
            <div className="max-h-48 overflow-y-auto space-y-2">
              {data.edges.slice(0, 20).map((edge, i) => (
                <div key={i} className="text-xs p-2 bg-amber-50 rounded border border-amber-200">
                  <div className="flex items-center gap-2 text-amber-700 font-medium mb-1">
                    <span>{Math.round(edge.similarity * 100)}% similar</span>
                  </div>
                  <p className="text-gray-700 line-clamp-1">1: {data.claims[edge.source]?.content}</p>
                  <p className="text-gray-700 line-clamp-1 mt-1">2: {data.claims[edge.target]?.content}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
